import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { getCandidateTimesheetHistoryThunk } from "../../../../store/timesheet/thunks";
import { PaginationLink } from "../../../../store/superadmin/types";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../../constants/app";
import { SortOrder } from "../../../../store/superadmin/referral/types";
import { TTimesheetSort } from "../../../../store/timesheet/types";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import ListFilter from "./ListFilter";
import ListHeader from "./ListHeader";
import ListItems from "./ListItems";

const TimesheetHistory: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { totalCandidateActiveTimeSheet } = useSelector(
    (state) => state.timesheet
  );
  const { candidatehistoryTimesheets } = useSelector(
    (state) => state.timesheet
  );

  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const startDateStr = thirtyDaysAgo.toISOString().split("T")[0];
  const endDateStr = today.toISOString().split("T")[0];

  const [searchInput, setSearchInput] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [centresTotalPages, setCentresTotalPages] = useState<number>(1);
  const [centresPaginationLinks, setCentresPaginationLinks] = useState<
    PaginationLink[]
  >([]);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const [selectedSortBy, setSelectedSortBy] =
    useState<TTimesheetSort>("time_frame");
  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>("desc");
  const [selectedCentre, setSelectedCentre] = useState<number[]>([]);
  const [selectedStartDate, setSelectedStartDate] =
    useState<string>(startDateStr);
  const [selectedEndDate, setSelectedEndDate] = useState<string>(endDateStr);
  const [hasUserSelectedRange, setHasUserSelectedRange] =
    useState<boolean>(false);

  useEffect(() => {
    const getCandidatesHistoryTimesheetList = async () => {
      setHasUserSelectedRange(false);
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        getCandidateTimesheetHistoryThunk({
          keyword: selectedKeyword,
          page: currentPage,
          per_page: perPage,
          sort_by: selectedSortBy,
          sort_order: selectedSortOrder,
          start_date: selectedStartDate,
          end_date: selectedEndDate,
          centre_id: selectedCentre,
        })
      );
      dispatch(hideTransparentPreloaderAction());
    };

    getCandidatesHistoryTimesheetList();
  }, [
    dispatch,
    selectedKeyword,
    currentPage,
    selectedSortBy,
    selectedSortOrder,
    perPage,
    hasUserSelectedRange,
    selectedCentre,
  ]);

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch); // Update the selected keyword state
      setCurrentPage(1); // Reset the current page to 1
    }
  }, [debouncedSearch]);

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (candidatehistoryTimesheets && candidatehistoryTimesheets?.total > 0) {
      setCentresTotalPages(candidatehistoryTimesheets?.last_page || 1);
      setCentresPaginationLinks(candidatehistoryTimesheets?.links);
    }
  }, [candidatehistoryTimesheets]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  // Handle sorting logic
  const handleSortChange = (field: any) => {
    if (selectedSortBy === field) {
      setSelectedSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSelectedSortBy(field);
      setSelectedSortOrder("asc");
    }
  };

  const handleTimesheet = () => {
    navigate(AUTH_ROUTE.TIMESHEET);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991);
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 991);
    }

    window.addEventListener("resize", handleResize);

    // Clean up listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column gap-10">
          <div className="d-flex flex-column">
            <div className="d-flex gap-10 align-items-center overflow-auto mobile-scroll-hide">
              <div
                className={`top-radius-10px ${
                  isMobile ? "py-13px" : "py-5px"
                } px-15px d-flex gap-5 align-items-center text-nowrap cursor-pointer bg-off-white text-black`}
                onClick={handleTimesheet}
              >
                <div className="fs-22 fs-mobile-16 fw-700">
                  {totalCandidateActiveTimeSheet}
                </div>
                <div className="fs-22 fs-mobile-16 fw-400">
                  Active Timesheet
                  {totalCandidateActiveTimeSheet !== 1 ? "s" : ""}
                </div>
              </div>
              <div
                className={`top-radius-10px ${
                  isMobile ? "py-13px" : "py-5px"
                } px-15px d-flex gap-5 align-items-center text-nowrap cursor-pointer bg-black text-white`}
              >
                <div className="fs-22 fs-mobile-16 fw-400">History</div>
              </div>
            </div>
            <div className="bg-black w-100 h-5px"></div>
          </div>
          <ListFilter
            setSearchInput={setSearchInput}
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            selectedCentre={selectedCentre}
            setSelectedCentre={setSelectedCentre}
            setHasUserSelectedRange={setHasUserSelectedRange}
            handleSortChange={handleSortChange}
          />
          <div className="d-flex flex-column">
            <ListHeader
              selectedSortBy={selectedSortBy}
              selectedSortOrder={selectedSortOrder}
              handleSortChange={handleSortChange}
            />
            <ListItems
              historyTimesheetLists={candidatehistoryTimesheets}
              currentPage={currentPage}
              totalPages={centresTotalPages}
              paginationLinks={centresPaginationLinks}
              onPageChange={handlePageChange}
              setPerPageList={setPerPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TimesheetHistory;
